import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import dataServicePage from "../schemas/dataServicePage";

const siteUrl = "https://ggsinergi.com";
const defaultImage = `${siteUrl}/photos/logo/logoCard.png`;

const routeMetadata = {
  "/home": {
    title: "Mineral Exploration & Drilling Services in Indonesia",
    description:
      "PT Green Geo Sinergi provides mineral exploration, coal and mineral drilling, geotechnical investigation, and geological mapping services in Indonesia.",
    image: `${siteUrl}/photos/services/ggs-photo-2.jpeg`,
  },
  "/about": {
    title: "About Green Geo Sinergi | Exploration Company",
    description:
      "Learn about PT Green Geo Sinergi, our experienced team, and our commitment to reliable mineral exploration and drilling services.",
    image: `${siteUrl}/photos/services/team-ggs.jpeg`,
  },
  "/workshop-detail": {
    title: "Drilling Equipment Workshop | Green Geo Sinergi",
    description:
      "Explore the Green Geo Sinergi workshop, where our drilling rigs are built, assembled, and maintained for demanding field conditions.",
    image: `${siteUrl}/photos/services/workshop.jpeg`,
  },
  "/project-list": {
    title: "Exploration Project Portfolio | Green Geo Sinergi",
    description:
      "Explore Green Geo Sinergi's mineral exploration and drilling project locations across Indonesia.",
    image: `${siteUrl}/photos/services/ggs-photo-1.jpeg`,
  },
  "/contact": {
    title: "Contact Green Geo Sinergi | Indonesia",
    description:
      "Contact PT Green Geo Sinergi in Balikpapan, Indonesia, about mineral exploration, drilling, geotechnical, and mapping projects.",
    image: defaultImage,
  },
};

const serviceDescriptions = {
  "/coal-and-mineral-drilling-services-detail":
    "Explore coal and mineral drilling services by Green Geo Sinergi, including touch coring, full coring, core recovery, and geological reporting in Indonesia.",
  "/geotechnical-drilling-and-soil-investigation-detail":
    "Green Geo Sinergi provides geotechnical drilling and soil investigation in Indonesia, including Standard Penetration Tests (SPT) and Cone Penetration Tests (CPT).",
  "/surface-mapping-detail":
    "Explore topographic and geological mapping services for coal, gold, nickel, and other mineral exploration projects across Indonesia.",
};

function setMetaTag(attribute, key, content) {
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
}

function setCanonical(pathname) {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  element.setAttribute("href", `${siteUrl}${pathname}`);
}

function getMetadata(pathname) {
  const service = dataServicePage.find((item) => item.pathName === pathname)?.datas;

  if (service) {
    const title = service.title
      .replace(/-detail$/i, "")
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");

    return {
      title: `${title} | Green Geo Sinergi`,
      description: serviceDescriptions[pathname],
      image: `${siteUrl}${service.src}`,
      indexable: true,
    };
  }

  const metadata = routeMetadata[pathname];

  if (metadata) {
    return { ...metadata, indexable: true };
  }

  return {
    title: "Page Not Found | Green Geo Sinergi",
    description: "The requested page could not be found.",
    image: defaultImage,
    indexable: false,
  };
}

export default function RouteSeo() {
  const { pathname } = useLocation();

  useEffect(() => {
    const metadata = getMetadata(pathname);
    const canonicalUrl = `${siteUrl}${pathname}`;
    const imageUrl = metadata.image || defaultImage;
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "PT Green Geo Sinergi",
      url: siteUrl,
      logo: `${siteUrl}/photos/logo/logoCard.png`,
      description:
        "Professional mineral exploration, drilling, geotechnical investigation, and geological mapping services in Indonesia.",
      email: "yusni@greengeosinergi.com",
      telephone: "+6281347033301",
      address: {
        "@type": "PostalAddress",
        addressLocality: "Balikpapan",
        addressCountry: "ID",
      },
    };

    document.title = metadata.title;
    setMetaTag("name", "description", metadata.description);
    setMetaTag("name", "robots", metadata.indexable ? "index,follow" : "noindex,follow");
    setMetaTag("property", "og:type", "website");
    setMetaTag("property", "og:site_name", "Green Geo Sinergi");
    setMetaTag("property", "og:title", metadata.title);
    setMetaTag("property", "og:description", metadata.description);
    setMetaTag("property", "og:url", canonicalUrl);
    setMetaTag("property", "og:image", imageUrl);
    setMetaTag("name", "twitter:card", "summary_large_image");
    setMetaTag("name", "twitter:title", metadata.title);
    setMetaTag("name", "twitter:description", metadata.description);
    setMetaTag("name", "twitter:image", imageUrl);
    setCanonical(pathname);

    let schemaElement = document.getElementById("ggs-organization-schema");
    if (!schemaElement) {
      schemaElement = document.createElement("script");
      schemaElement.id = "ggs-organization-schema";
      schemaElement.type = "application/ld+json";
      document.head.appendChild(schemaElement);
    }
    schemaElement.textContent = JSON.stringify(organizationSchema);
  }, [pathname]);

  return null;
}
